import { useState, useEffect } from "react";
import cameraService, { DeploymentInfo } from "../services/cameraService";

const FETCH_LIMIT = 2000;
const HOUR = 1000 * 3600;
const LIMIT = 100;
const GROUPING_INTERVAL = 15; //Seconds
const IMAGE_HEADER = "https://";

interface MeasurementPoint {
  timestamp: Date;
  type:
    | "Densities"
    | "DensityHeatmaps"
    | "Flows"
    | "FlowHeatmaps"
    | "Headcounts"
    | "Images"
    | "Moods"
    | "MoodHeatmaps";
  value: number; //Density, mood, flow , velocity etc. measurements
  text: string; //Used for URLS, etc.
}

export interface MeasurementState {
  timestamp: Date;
  density: number;
  mood: number;
  flow: number;
  heatmap: string;
  image: string;
}

interface MeasurementCache {
  [deploymentId: string]: MeasurementPoint[];
}

interface LiveMeasurementState {
  [deploymentId: string]: MeasurementState;
}

interface ReducedMeasurementState {
  [deploymentId: string]: MeasurementState[];
}

export type initializeReturnType = ReturnType<typeof initialize>;

export type CameraHook = ReturnType<typeof useCameras>;
/**
 * Contains all the hooks for camera information and data
 */
export function useCameras(tenantId: string) {
  const [deployments, setDeployments] = useState<DeploymentInfo[]>([]);
  const [liveState, setLiveState] = useState<LiveMeasurementState>({}); //live Values
  const [cache, setCache] = useState<MeasurementCache>({}); //Cache of time-key-value triplets
  const [reducedValues, setReducedValues] = useState<ReducedMeasurementState>(
    {}
  );

  //Historical data states
  const [pastState, setPastState] = useState<LiveMeasurementState>({});
  const [pastCache, setPastCache] = useState<MeasurementCache>({});
  const [pastReducedValues, setPastReducedValues] = useState<
    ReducedMeasurementState
  >({});
  const [pastCacheStart, setPastCacheStart] = useState<Date>();
  const [pastCacheEnd, setPastCacheEnd] = useState<Date>();

  //For refetching logic
  const [cacheStart, setCacheStart] = useState<Date>();
  const [cacheEnd, setCacheEnd] = useState<Date>();
  const [startIndex, setStartIndex] = useState<number>(0);
  //when refetching data, data is appended to cache + reducedValues array
  //1.Data from cache is deleted if its before cacheStart Date
  //2.Data from reducedArray is changed to undefined (preserves index) but GCs data
  //3.startIndex refers to the start of data that is avaiable, preventing undefined values

  const fetchMoreData = async () => {
    //1.Look at the most recently fetched timestamp (cacheEnd)
    //2.Fetch values 15s past this time
    const fromTime = cacheEnd || new Date();
    const toTime = new Date(+fromTime + 1000 * GROUPING_INTERVAL);
    //3.Foreach deploymentId we fetch more data from it
    const promises = deployments.map(async deployment => {
      const id = deployment.deploymentId;
      //For each deploymentId we will grab their measurements
      const data = await getMeasurements({
        //#ff0 temporary change to combine data
        deploymentIds: id === 3 ? [3, 4] : [id],
        // deploymentIds: [id],
        fromTime,
        toTime,
        limit: LIMIT,
        offset: 0,
      });
      const sortedData = data.sort((a, b) => {
        return +a.timestamp - +b.timestamp; //Sort ascending timestamps
      });
      //Probably not very efficient below
      const reducedState = reduceMeasurements({
        cache: sortedData,
        startTime: fromTime,
        endTime: toTime,
      }).map(state => {
        //If no new readings, use previous measurements
        if (
          !state.density &&
          !state.flow &&
          !state.mood &&
          !state.image &&
          !state.heatmap &&
          reducedValues[id].length > 0
        ) {
          const prevValue = reducedValues[id][reducedValues[id].length - 1];
          console.log("previous value that was replaced:", prevValue);

          if (prevValue) return { ...prevValue, timestamp: toTime };
          return state;
        } else {
          return state;
        }
      });
      //4.Add it to cache + generate reducedData and add to reducedArray
      // cache[id] = [...cache[id], ...sortedData]; //Dont add to cache to save memory
      // reducedValues[id] = [...reducedValues[id], ...reducedState];

      //5.Delete old data + increase the start index
      // if (cacheStart) {
      //   setCacheStart(new Date(+cacheStart + GROUPING_INTERVAL * 1000));
      // } //Disabled for now, no need for a cache start time

      //#0f0 MOVE THIS OUT OF LOOP and in one state update
      //Change state in array without triggering state change.
      // reducedState.forEach((v, i) => {
      //   //@ts-ignore
      //   reducedValues[id][i] = undefined; // Mark as undefined (but dont access!!)
      // });

      if (reducedState.length > 0) {
        //Return the new data recieved
        return { data: reducedState, id };
      }
    });
    console.log("More recent data has been fetched...", toTime);
    const newData = await Promise.all(promises);
    //await all the new data before setting it as live data
    let newLiveState: LiveMeasurementState = {};
    let newReducedValues: ReducedMeasurementState = {};
    let newDataLength: number = 0;
    newData
      .filter(v => v && !!v.data)
      .forEach(v => {
        const newValues = v!;
        if (newValues.data.length > 0) {
          newLiveState[newValues.id] =
            newValues.data[newValues.data.length - 1];
        }
        newReducedValues[newValues.id] = [
          ...reducedValues[newValues.id],
          ...newValues.data,
        ];
        //Set the newDataLength to highest one recieved (should be same anyway)
        newDataLength =
          v!.data.length > newDataLength ? v!.data.length : newDataLength;
        //Mark older data as undefiend but keep index
        for (let i = 0; i < newDataLength; i++) {
          //@ts-ignore
          newReducedValues[newValues.id][i] = undefined;
        }
      });
    setStartIndex(startIndex + newDataLength);
    setReducedValues(newReducedValues);
    setLiveState(newLiveState);
    if (cacheEnd) {
      setCacheEnd(new Date(+cacheEnd + GROUPING_INTERVAL * 1000));
    }
  };

  const setData = (initialValues: any) => {
    const {
      deployments,
      measurementCache,
      reducedValues,
      start,
      end,
    } = initialValues;
    //Load all initial values after query
    setDeployments(deployments);
    setCache(measurementCache);
    setReducedValues(reducedValues);
    setCacheStart(start);
    setCacheEnd(end);

    //If there are reduced Values, we will take last slice of each as the current live state
    let liveStates: LiveMeasurementState = {};
    Object.keys(reducedValues).forEach(key => {
      const states = reducedValues[key];
      if (states.length > 0) {
        liveStates[key] = states[states.length - 1];
      }
    });
    setLiveState(liveStates);
  };

  const setPastData = (initialValues: any) => {
    const {
      deployments,
      measurementCache,
      reducedValues,
      start,
      end,
    } = initialValues;
    //Load all initial values after query
    setPastCache(measurementCache);
    setPastReducedValues(reducedValues);
    setPastCacheStart(start);
    setPastCacheEnd(end);

    //If there are reduced Values, we will take last slice of each as the current live state
    let liveStates: LiveMeasurementState = {};
    Object.keys(reducedValues).forEach(key => {
      const states = reducedValues[key];
      if (states.length > 0) {
        liveStates[key] = states[states.length - 1];
      }
    });
    setPastState(liveStates);
  };

  const resetPastData = async (time: Date) => {
    console.log("Fetching more historical data...");
    return await initialize(tenantId, time).then(setPastData);
  };

  //Initial load
  useEffect(() => {
    // const time = new Date("2019-06-10.20:10:00"); //For picking a good reference spot before
    const time = new Date();
    initialize(tenantId, time).then(setData);
  }, []);

  const data = {
    deployments,
    liveState,
    cache,
    startIndex,
    reducedValues,
    cacheStart,
    cacheEnd,
    //PAST STATES
    pastState,
    pastCache,
    pastReducedValues,
    pastCacheStart,
    pastCacheEnd,
  };
  const methods = { resetPastData, fetchMoreData };

  return { data, methods };

  //Build function that initialises
  //1. get all the deployment information
  // Group deployments into [1,2] etc. X- no need for this anymore
  //2. query the initial 1 hour of data from 1 hour ago to live.
  // Will need to loop through until theres no more data.
  //3. generate the reduced values for each deploymentId
  //4. generates the live states for each deployment
  //5. return all these values at once to populate states in initial useEffect hook

  //ON deployments UPDATE:
  // Run the initialisation function for that deploymentId and set the state again.

  //when there are new data updates, it will trigger change in cache,

  //ON cache UPDATE:
  //1. compare cacheStart & cacheEnd pointers with new data (this marks the cached values used for reduced values)
  //2. If new data is 15 seconds newer, then we want to generate reduced values for each 15s chunk
  //3. delete the number of chunks off reduced values equal to the amount of new entries
}

async function initialize(tenantId: string, time: Date) {
  const deployments = await getDeployments(tenantId);
  const toTime = time;
  // const toTime = new Date("2019-06-10.20:10:00");
  // const toTime = new Date();
  const fromTime = new Date(toTime.getTime() - HOUR);
  const limit = LIMIT;
  const offset = 0; //Start from 0

  let measurementCache: MeasurementCache = {};
  let reducedValues: ReducedMeasurementState = {};
  const promises = deployments.map(async deployment => {
    const id = deployment.deploymentId;
    //For each deploymentId we will grab their measurements for the last hour
    const data = await getMeasurements({
      //#ff0 temporary change to combine data
      deploymentIds: id === 3 ? [3, 4] : [id],
      // deploymentIds: [id],
      fromTime,
      toTime,
      limit,
      offset,
    });
    const sortedData = data.sort((a, b) => {
      return +a.timestamp - +b.timestamp; //Sort ascending timestamps
    });
    const reducedState = reduceMeasurements({
      cache: sortedData,
      startTime: fromTime,
      endTime: toTime,
    });
    measurementCache[id] = sortedData;
    reducedValues[id] = reducedState;
  });
  await Promise.all(promises.map(p => p.catch(e => console.log(e)))); //Just catch and console log failed measurement queries

  return {
    deployments,
    measurementCache,
    reducedValues,
    start: fromTime,
    end: toTime,
  };
}

async function getDeployments(tenantId: string): Promise<DeploymentInfo[]> {
  try {
    const result = await cameraService.getDeployments({ tenantId });

    return result.deployments.deployments;
  } catch (error) {
    console.log(error);
    return [];
  }
}

async function getMeasurements(input: {
  deploymentIds: number[];
  fromTime: Date;
  toTime: Date;
  limit: number;
  offset: number;
}) {
  let results: MeasurementPoint[] = []; //Will keep adding results until theres no more
  let stop = false;
  for (let i = 0; i < FETCH_LIMIT && !stop; i = i + 100) {
    try {
      const result = await cameraService.getMeasurements({
        ...input,
        offset: i,
      });
      results = results.concat(
        result.data.map(v => ({
          ...v,
          type: v.type as any,
          timestamp: new Date(+v.timestamp),
        }))
      );
      if (!result.more) stop = true;
    } catch (error) {
      console.log(error);
      stop = true; //Will stop to prevent too many loops
    }
  }
  return results;
}

function reduceMeasurements(inputs: {
  cache: MeasurementPoint[];
  startTime: Date; //Start of where to generate reduced values
  endTime: Date; //End of where to generate reduced values
}) {
  const { endTime, startTime, cache } = inputs;
  const loops = ~~((+endTime - +startTime) / (GROUPING_INTERVAL * 1000));

  let measurements: MeasurementState[] = [];
  for (let i = 0; i < loops; i++) {
    const from = new Date(+startTime + i * GROUPING_INTERVAL * 1000); //(e.g. starting at t = 0s)
    const to = new Date(+startTime + (i + 1) * GROUPING_INTERVAL * 1000); //(e.g. starting at t = 15s)
    const initialState: MeasurementState = {
      timestamp: to,
      density: 0,
      mood: 0,
      flow: 0,
      heatmap: "",
      image: "",
    };
    const filteredRange = cache.filter(
      v => v.timestamp > from && v.timestamp <= to
    );

    const aggregatedState: MeasurementState = filteredRange.reduce(
      (acc: MeasurementState, curr: MeasurementPoint): MeasurementState => {
        const image = curr.text.includes(IMAGE_HEADER) ? curr.text : acc.image;

        switch (curr.type) {
          case "DensityHeatmaps":
            const heatmap = curr.text.includes(IMAGE_HEADER)
              ? curr.text
              : acc.heatmap;
            return { ...acc, heatmap };
          case "Images":
            return { ...acc, image };
          case "Densities":
            const density = curr.value > acc.density ? curr.value : acc.density;
            return { ...acc, density, image };
          case "Flows":
            const flow = curr.value > acc.flow ? curr.value : acc.flow;
            return { ...acc, flow, image };
          case "Moods":
            const mood = curr.value > acc.mood ? curr.value : acc.mood;
            return { ...acc, mood, image };
          // case "FlowHeatmaps":
          //   break;
          // case "MoodHeatmaps":
          //   break;
          // case "Headcounts":
          //   break;

          default:
            return acc;
        }
      },
      initialState
    );

    //CARRY OVER
    //If aggregated state did not change at all, and there is a previous value,
    //Use that previous value instead
    //#FF0 This was the previous code before fix
    // if (
    //   !aggregatedState.density &&
    //   !aggregatedState.flow &&
    //   !aggregatedState.mood &&
    //   !aggregatedState.image &&
    //   !aggregatedState.heatmap &&
    //   measurements.length > 0 &&
    //   i - 1 >= 0 //Make sure previous index accessible
    // ) {
    //   measurements = measurements.concat({
    //     ...measurements[i - 1],
    //     timestamp: to,
    //   });
    // } else {
    //   measurements = measurements.concat(aggregatedState);
    // }

    //#FF0 redo for the aggregated state values (carry over measurements individually)
    //Only retry if there is even a previous measurement to do
    if (measurements.length > 0 && i - 1 >= 0) {
      const prevMeasurement = measurements[i - 1];
      const combinedAggregatedState: MeasurementState = {
        timestamp: to,
        density: aggregatedState.density
          ? aggregatedState.density
          : prevMeasurement.density,
        mood: aggregatedState.mood
          ? aggregatedState.mood
          : prevMeasurement.mood,
        flow: aggregatedState.flow
          ? aggregatedState.flow
          : prevMeasurement.flow,
        image: aggregatedState.image
          ? aggregatedState.image
          : prevMeasurement.image,
        heatmap: aggregatedState.heatmap
          ? aggregatedState.heatmap
          : prevMeasurement.heatmap,
      };
      measurements = measurements.concat(combinedAggregatedState);
    } else {
      measurements = measurements.concat(aggregatedState);
    }
  }

  return measurements;
}
